// accountStyle.ts — Account-flow design tokens + reusable Tailwind
// class strings for the email-flow surface.
//
// Why a separate module? The rest of middleware-ui uses PrimeTime's
// existing theme (CSS variables like --Primetime-main, --Warning, etc.).
// Our new email components want a calmer, more refined aesthetic that
// signals "this is a security-sensitive flow, take your time". Rather
// than fork the whole theme, we keep these tokens local to the email
// surface only — everything else stays on the existing PrimeTime palette.
//
// Design language references a calm, refined aesthetic:
//   - Background:      warm off-white (#FAF7F2)
//   - Surface:         soft cream (#F5F0E8) for cards within
//   - Primary accent:  warm terracotta (#CC785C)
//   - Primary text:    near-black (#1F1F1F)
//   - Secondary text:  warm grey (#6B6258)
//   - Border:          subtle warm border (#E8E0D5)
//   - Border radius:   rounded-2xl (16px) for cards, rounded-full for CTAs
//   - Shadows:         soft + warm (shadow-sm with custom warm tint)
//   - Typography:      already-installed Roboto, slightly larger sizes
//                      and tighter tracking for a refined feel
//   - Spacing:         generous — 24px / 32px gutters where existing
//                      app uses 8px / 16px

// ---------------------------------------------------------------------------
// Colour tokens (inline-Tailwind compatible)
// ---------------------------------------------------------------------------

export const accountColors = {
  bg:				'#FAF7F2',
  surface:			'#F5F0E8',
  surfaceHover:		'#EEE8DD',
  border:			'#E8E0D5',
  primary:			'#CC785C',
  primaryHover:		'#B86848',
  primaryActive:	'#A35538',
  text:				'#1F1F1F',
  textMuted:		'#6B6258',
  textFaint:		'#9C9389',
  success:			'#5B7C5A',
  danger:			'#B14545',
  warningSoft:		'#F4E4D0',
  warningText:		'#8A5028',
} as const;

// ---------------------------------------------------------------------------
// Reusable className strings. Bracket-notation Tailwind classes inline
// the colours above so the build doesn't need new tailwind.config entries.
// ---------------------------------------------------------------------------

// modalSurface — the inner panel of every email-flow modal. Replaces
// the default HeroUI ModalBody styling with a calm cream surface.
export const cs = {
  // Layout
  modalPanel:	'min-w-[460px] max-w-[520px] p-[32px] bg-[#FAF7F2] rounded-2xl',
  card:			'bg-[#F5F0E8] border-solid border-1 border-[#E8E0D5] rounded-2xl',

  // Typography
  title:		'text-[24px] leading-[1.25] font-semibold text-[#1F1F1F] tracking-tight',
  subtitle:		'text-[15px] leading-[1.5] text-[#6B6258]',
  body:			'text-[14px] leading-[1.55] text-[#1F1F1F]',
  bodyMuted:	'text-[14px] leading-[1.55] text-[#6B6258]',
  caption:		'text-[12px] leading-[1.4] text-[#9C9389]',
  errorText:	'text-[13px] leading-[1.4] text-[#B14545]',

  // Buttons — built as className strings so callers can pass to <Button className={...}>
  // or directly to a <button>. Boxed (small radius), generous padding, subtle hover.
  primaryBtn:
    'px-[20px] py-[10px] bg-[--Primetime-main] hover:opacity-90 active:opacity-80 ' +
    'text-white font-medium text-[14px] rounded-[4px] ' +
    'transition-opacity duration-150 cursor-pointer ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',

  ghostBtn:
    'px-[20px] py-[10px] bg-transparent hover:bg-[#EEE8DD] ' +
    'text-[#1F1F1F] font-medium text-[14px] rounded-[4px] ' +
    'border-solid border-1 border-[#E8E0D5] ' +
    'transition-colors duration-150 cursor-pointer ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',

  textBtn:
    'text-[#CC785C] hover:text-[#B86848] hover:underline cursor-pointer ' +
    'font-medium text-[13px] transition-colors duration-150',

  // Input — applied via HeroUI Input's classNames prop
  inputWrapper:
    'px-[16px] py-[12px] bg-white border-solid border-1 border-[#E8E0D5] ' +
    'rounded-xl data-[hover=true]:border-[#CC785C]/40 ' +
    'data-[focus=true]:border-[#CC785C] data-[focus=true]:bg-white',
  inputLabel:	'text-[13px] font-medium text-[#1F1F1F]',
  inputInput:	'text-[15px] text-[#1F1F1F] placeholder:text-[#9C9389]',

  // Code input (centred, monospace, large)
  codeInputWrapper:
    'px-[16px] py-[14px] bg-white border-solid border-1 border-[#E8E0D5] ' +
    'rounded-xl data-[focus=true]:border-[#CC785C]',
  codeInputInput:
    'text-[22px] font-mono tracking-[0.4em] text-center text-[#1F1F1F]',

  // Banner — warm, attention-getting without being alarming
  bannerWarm:
    'w-full bg-[#F4E4D0] text-[#8A5028] px-[20px] py-[12px] ' +
    'border-b-1 border-solid border-[#E8C8A8]',
  bannerLink:	'text-[#8A5028] underline font-semibold hover:text-[#5C3818] cursor-pointer',

  // Hairline divider with warm tint
  divider:		'w-full h-[1px] bg-[#E8E0D5]',

  // Pill chip (e.g. "expires in 9:34")
  countdownChip:
    'inline-flex items-center gap-[6px] px-[12px] py-[6px] ' +
    'bg-[#F5F0E8] text-[#6B6258] text-[12px] font-medium rounded-full ' +
    'border-solid border-1 border-[#E8E0D5]',
} as const;

// Convenience for the modal backdrop (used as MModal classNames.backdrop)
export const accountBackdropClass = 'bg-[#1F1F1F]/30 backdrop-blur-sm';
